<template>
  <div class="product-container">
    <!-- 筛选条件 -->
    <el-card class="filter-card" shadow="never">
      <el-form :inline="true" :model="filters" class="filter-form">
        <!-- 关键词搜索 -->
        <el-form-item label="关键词">
          <el-input
            v-model="filters.keyword"
            placeholder="商品名称/编码"
            clearable
            style="width: 200px"
            @keyup.enter.native="handleFilter"
          />
        </el-form-item>

        <!-- 状态筛选 -->
        <el-form-item label="商品状态">
          <el-select
            v-model="filters.status"
            placeholder="请选择"
            clearable
            style="width: 150px"
          >
            <el-option label="全部" :value="undefined" />
            <el-option label="上架" :value="1" />
            <el-option label="下架" :value="0" />
          </el-select>
        </el-form-item>

        <!-- 操作按钮 -->
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" @click="handleFilter">
            查询
          </el-button>
          <el-button icon="el-icon-refresh-left" @click="handleReset">
            重置
          </el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 数据表格 -->
    <el-card class="table-card" shadow="never" style="margin-top: 20px">
      <!-- 顶部按钮 -->
      <div style="margin-bottom: 20px">
        <el-button type="primary" icon="el-icon-plus" @click="handleCreate">
          新增商品
        </el-button>
      </div>

      <el-table
        v-loading="listLoading"
        :data="productList"
        border
        fit
        highlight-current-row
        style="width: 100%"
      >
        <!-- ID -->
        <el-table-column
          prop="id"
          label="ID"
          width="80"
          align="center"
        />

        <!-- 商品编码 -->
        <el-table-column
          prop="product_code"
          label="商品编码"
          width="150"
          align="center"
        />

        <!-- 商品名称 -->
        <el-table-column
          prop="product_name"
          label="商品名称"
          width="200"
          align="center"
          show-overflow-tooltip
        />

        <!-- 价格 -->
        <el-table-column
          prop="price"
          label="价格(元)"
          width="120"
          align="center"
        >
          <template slot-scope="{ row }">
            <span>¥{{ formatMoney(row.price) }}</span>
          </template>
        </el-table-column>

        <!-- 默认分佣类型 -->
        <el-table-column
          prop="default_commission_type"
          label="分佣类型"
          width="120"
          align="center"
        >
          <template slot-scope="{ row }">
            {{ getCommissionTypeText(row.default_commission_type) }}
          </template>
        </el-table-column>

        <!-- 默认分佣值 -->
        <el-table-column
          prop="default_commission_value"
          label="分佣值"
          width="120"
          align="center"
        >
          <template slot-scope="{ row }">
            {{ formatCommissionValue(row.default_commission_type, row.default_commission_value) }}
          </template>
        </el-table-column>

        <!-- 收款商户 -->
        <el-table-column
          prop="merchant_name"
          label="收款商户"
          width="180"
          align="center"
          show-overflow-tooltip
        >
          <template slot-scope="{ row }">
            <span v-if="row.merchant_name">{{ row.merchant_name }}</span>
            <span v-else style="color: #909399">系统默认</span>
          </template>
        </el-table-column>

        <!-- 状态 -->
        <el-table-column
          prop="status"
          label="状态"
          width="100"
          align="center"
        >
          <template slot-scope="{ row }">
            <el-tag :type="getProductStatusTagType(row.status)" size="small">
              {{ getProductStatusText(row.status) }}
            </el-tag>
          </template>
        </el-table-column>

        <!-- 创建时间 -->
        <el-table-column
          prop="created_at"
          label="创建时间"
          width="180"
          align="center"
        >
          <template slot-scope="{ row }">
            {{ formatDate(row.created_at) }}
          </template>
        </el-table-column>

        <!-- 操作列 -->
        <el-table-column
          label="操作"
          width="300"
          align="center"
          fixed="right"
        >
          <template slot-scope="{ row }">
            <el-button type="text" size="small" @click="handleEdit(row)">
              编辑
            </el-button>
            <el-button
              type="text"
              size="small"
              @click="handleToggleStatus(row)"
            >
              {{ row.status === 1 ? '下架' : '上架' }}
            </el-button>
            <el-button
              type="text"
              size="small"
              style="color: #409EFF"
              @click="handlePackages(row)"
            >
              套餐管理
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination-container">
        <el-pagination
          :current-page="listQuery.page"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="listQuery.page_size"
          :total="total"
          background
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </el-card>

    <!-- 新增/编辑商品弹窗 -->
    <product-dialog
      ref="productDialog"
      @success="fetchProductList"
    />
  </div>
</template>

<script>
import { getProductList, updateProductStatus } from '@/api/iccid-product'
import ProductDialog from './components/ProductDialog'
import {
  getProductStatusText,
  getProductStatusTagType,
  getCommissionTypeText,
  formatCommissionValue,
  formatMoney,
  formatDate
} from '@/utils/iccid-formatter'

export default {
  name: 'IccidProductList',
  components: {
    ProductDialog
  },
  data() {
    return {
      productList: [],
      total: 0,
      listLoading: false,
      listQuery: {
        page: 1,
        page_size: 20
      },
      filters: {
        keyword: '',
        status: undefined
      }
    }
  },
  created() {
    this.fetchProductList()
  },
  methods: {
    // 格式化函数
    formatMoney,
    formatDate,
    getProductStatusText,
    getProductStatusTagType,
    getCommissionTypeText,
    formatCommissionValue,

    // 获取商品列表
    async fetchProductList() {
      this.listLoading = true
      try {
        const params = {
          ...this.listQuery,
          ...this.filters
        }
        const { data } = await getProductList(params)
        this.productList = data.list || []
        this.total = data.total || 0
      } catch (error) {
        this.$message.error('获取商品列表失败')
      } finally {
        this.listLoading = false
      }
    },

    // 查询
    handleFilter() {
      this.listQuery.page = 1
      this.fetchProductList()
    },

    // 重置
    handleReset() {
      this.filters = {
        keyword: '',
        status: undefined
      }
      this.listQuery.page = 1
      this.fetchProductList()
    },

    // 分页
    handleSizeChange(val) {
      this.listQuery.page_size = val
      this.fetchProductList()
    },
    handleCurrentChange(val) {
      this.listQuery.page = val
      this.fetchProductList()
    },

    // 新增商品
    handleCreate() {
      this.$refs.productDialog.open()
    },

    // 编辑商品
    handleEdit(row) {
      this.$refs.productDialog.open(row)
    },

    // 切换状态
    handleToggleStatus(row) {
      const status = row.status === 1 ? 0 : 1
      const statusText = status === 1 ? '上架' : '下架'

      this.$confirm(`确认${statusText}该商品吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        return updateProductStatus({
          id: row.id,
          status: status
        })
      }).then(() => {
        this.$message.success(`${statusText}成功`)
        this.fetchProductList()
      }).catch(() => {})
    },

    // 套餐管理
    handlePackages(row) {
      this.$router.push({
        name: 'IccidPackageList',
        params: { productCode: row.product_code }
      })
    }
  }
}
</script>

<style scoped>
.product-container {
  padding: 20px;
}

.filter-card,
.table-card {
  margin-bottom: 20px;
}

.filter-form {
  margin-bottom: -18px;
}

.pagination-container {
  margin-top: 20px;
  text-align: right;
}
</style>
